import {
  Box,
  Card,
  Collapse,
  Grid,
  List,
  ListItemButton,
  SearchBar,
  Stack,
  Typography,
  useElementBreakpoints,
} from "@compass/core";
import produce, { original } from "immer";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { UserManualSectionDto, UserManualSubSectionDto } from "generated/openApi";
import { useNavigate, Outlet } from "react-router-dom";
import { useState, useEffect } from "react";

type UserManualPageBodyProps = {
  sections: UserManualSectionDto[] | undefined;
};

export const UserManualPageBody = ({ sections }: UserManualPageBodyProps) => {
  const navigate = useNavigate();
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [filteredSections, setFilteredSections] = useState<UserManualSectionDto[] | undefined>(sections);

  useEffect(() => {
    setFilteredSections(sections);
  }, [sections]);

  const selectSubSection = (subSection: UserManualSubSectionDto) => {
    navigate({
      pathname: subSection.uniqueIdentifier,
    });
  };

  const expandSection = (section: UserManualSectionDto) => {
    setFilteredSections(
      produce((draft) => {
        let draftSection = draft?.find((x) => original(x) === section)!;
        draftSection.expanded = !draftSection.expanded;
      })
    );
  };

  const searchSubSections = (searchString: string) => {
    const formattedSearchString: string = searchString.toLowerCase();

    const filteredSections: any = [];

    sections!.map((s) => {
      if (
        s.name!.toLowerCase().includes(formattedSearchString) ||
        s.subSections?.some(
          (ss) =>
            ss.name!.toLowerCase().includes(formattedSearchString) ||
            ss.tags?.some((t) => t.name!.toLowerCase().includes(formattedSearchString))
        )
      ) {
        var tempCopy = produce(s, (draft) => {
          draft.expanded = true;
          draft.subSections = draft.subSections?.filter(
            (ss) =>
              ss.name!.toLowerCase().includes(formattedSearchString) ||
              ss.tags?.some((t) => t.name!.toLowerCase().includes(formattedSearchString))
          );
        });

        filteredSections.push(tempCopy);
      }
    });

    setFilteredSections(filteredSections);
  };

  const renderSubSections = (subSections: UserManualSubSectionDto[] | undefined, expanded: boolean | undefined) => {
    if (subSections && subSections.length >= 1) {
      return (
        <Collapse in={expanded} orientation="vertical">
          <Box px={3} fontSize="0.9rem">
            <List>
              {subSections.map((subMenuItem) => {
                return (
                  <Box key={subMenuItem.name}>
                    <ListItemButton key={subMenuItem.name} onClick={() => selectSubSection(subMenuItem)}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="body1">{subMenuItem.name}</Typography>
                      </Stack>
                    </ListItemButton>
                  </Box>
                );
              })}
            </List>
          </Box>
        </Collapse>
      );
    }

    return null;
  };

  return (
    <Grid ref={breakpointsRef} container spacing="lg">
      <Grid item xs={setBreakpoints({ md: 3, xs: 12 })}>
        <Card>
          {!filteredSections && (
            <Box display="flex" height="100%" alignItems={"center"} justifyContent="center">
              <Typography variant="body1">No Sections have been added by Cobro staff yet.</Typography>
            </Box>
          )}
          <List>
            <SearchBar
              onStoppedTyping={(searchString) => searchSubSections(searchString)}
              setSearchFocused={setSearchFocused}
              size="small"
            />
            {filteredSections?.map((section) => (
              <Box key={section.name}>
                <ListItemButton sx={{ display: "block" }} onClick={() => expandSection(section)}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" px={1} minHeight="40px">
                    <Typography fontWeight="medium">{section.name}</Typography>

                    {section.subSections && section.subSections.length >= 1 && (
                      <KeyboardArrowUpIcon
                        sx={{
                          transform: section.expanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "all 0.2s",
                        }}
                      />
                    )}
                  </Stack>
                </ListItemButton>
                {renderSubSections(section.subSections, section.expanded)}
              </Box>
            ))}
          </List>
          <Stack display="flex" direction={"column"} spacing="md">
            <Box></Box>
          </Stack>
        </Card>
      </Grid>
      
      <Grid item xs={setBreakpoints({ md: 9, xs: 12 })}>
        <Outlet />
      </Grid>
    </Grid>
  );
};
